/**
 * One person's signed-in devices, and their own allowance.
 *
 *   <UserDevices userId={u.id} value={override} onChange={setOverride} />
 *
 * The number here overrides whatever the organisation allows that role — in both
 * directions, so a shared account can be tied to one device and a head teacher can be
 * let off entirely. The list underneath is what an admin actually needs when somebody
 * says "it keeps signing me out": nearly always an old phone still holding a slot.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Devices, SignOut, DeviceMobile } from '@phosphor-icons/react';

const INHERIT = '';          // the field is empty → use the role's number
const UNLIMITED = 0;

const when = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (Number.isNaN(diff)) return '';
  if (diff < 90) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  return new Date(iso).toLocaleDateString();
};

export default function UserDevices({ userId, value, onChange }) {
  const { api } = useAuth();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await api.get(`/users/${userId}/sessions`);
      setInfo(res.data);
    } catch {
      setInfo(null);
    }
  }, [api, userId]);

  useEffect(() => { load(); }, [load]);

  const signOutEverywhere = async () => {
    if (!window.confirm('Sign this user out on every device? They will need to sign in again.')) return;
    setBusy(true);
    try {
      const res = await api.delete(`/users/${userId}/sessions`);
      toast.success(res.data?.message || 'Signed out');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not sign them out');
    } finally {
      setBusy(false);
    }
  };

  const rows = info?.sessions || [];
  const roleLimit = info?.role_limit;

  return (
    <div className="space-y-2">
      <div className="field">
        <Label htmlFor="max_sessions" data-find="Devices allowed">Devices allowed at once</Label>
        <Input
          id="max_sessions"
          type="number"
          min="0"
          max="20"
          className="w-[140px]"
          placeholder={roleLimit ? `${roleLimit} (their role)` : 'No limit'}
          value={value === null || value === undefined ? INHERIT : value}
          onChange={(e) => {
            const raw = e.target.value;
            onChange(raw === '' ? null : Math.max(0, Math.min(20, parseInt(raw, 10) || 0)));
          }}
          data-testid="user-max-sessions"
        />
        <p className="field-hint">
          {value === null || value === undefined
            ? (roleLimit ? `Using the organisation's number for their role: ${roleLimit}.` : 'No limit set for their role.')
            : value === UNLIMITED
              ? 'No limit for this person, whatever their role allows.'
              : `Just for this person, instead of their role's number${roleLimit ? ` of ${roleLimit}` : ''}.`}
          {' '}Leave it empty to follow the role.
        </p>
      </div>

      <div className="rounded-md border border-border">
        <div className="flex items-center gap-2 px-3 h-8 border-b border-border bg-muted/30">
          <Devices className="w-4 h-4 text-shell-primary" />
          <span className="text-[12px] font-semibold mr-auto" data-find="Signed in on">
            Signed in on {rows.length} device{rows.length === 1 ? '' : 's'}
          </span>
          {rows.length > 0 && (
            <Button type="button" size="sm" variant="outline" className="h-7" disabled={busy}
              onClick={signOutEverywhere} data-testid="sign-out-everywhere">
              <SignOut className="w-3.5 h-3.5" /> Sign out everywhere
            </Button>
          )}
        </div>
        {rows.length === 0 ? (
          <p className="px-3 py-3 text-[12px] text-muted-foreground" data-testid="no-devices">
            Not signed in anywhere at the moment.
          </p>
        ) : (
          <ul className="divide-y divide-border" data-testid="device-list">
            {rows.map((s) => (
              <li key={s.id} className="flex items-center gap-2 px-3 py-2 text-[12px]">
                <DeviceMobile className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                <span className="font-medium min-w-0 truncate">{s.device || 'Device'}</span>
                <span className="text-muted-foreground ml-auto whitespace-nowrap">{when(s.last_seen_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
